import React, { useMemo } from 'react'
import styles from '@/styles/Checkbox.module.scss'

export type CheckboxProps = {
  form ?: Record<string, any>,
  name ?: string,
  onChange ?: (name: string, value: boolean, childKey ?: string) => void,
  switchMode ?: boolean,
  onSet ?: (value: boolean) => void,
  value ?: boolean,
  radioValue ?: any,
  error ?: string,
  errors ?: Record<string, any>,
  labelClass ?: string,
  children ?: React.ReactNode | React.ReactNode[],
  eClass ?: string,
  label ?: string | React.ReactNode,
  onlyInput ?: boolean,
  readOnly ?: boolean,
  className ?: string,
  inputType ?: string
}

const GCheckBox = (props : CheckboxProps) => {
  const {
    form,
    name,
    onChange,
    switchMode,
    onSet,
    readOnly,
    value,
    error,
    radioValue,
    errors,
    inputType,
    labelClass,
    children,
    eClass,
    label,
    onlyInput,
    className
  } = props
  const checked = useMemo(() => {
    if (form && form[name]) {
      return form[name]
    }
    return value
  }, [form, name, value])

  const inputError = useMemo(() => {
    if (errors && errors[name]) {
      return errors[name]
    }
    return error
  }, [errors, name, error])

  const onFormChange = (e) => {
    if (readOnly) {
      return false
    }
    if (inputType === 'radio') {
      if (onSet) {
        return onSet(e.target.value)
      }
      if (onChange) {
        onChange(name, e.target.value)
      }
    } else {
      if (onSet) {
        return onSet(e.target.checked)
      }
      if (onChange) {
        onChange(name, e.target.checked)
      }
    }
    return false
  }

  const onToggle = (e) => {
    if (readOnly) {
      return false
    }
    // e.preventDefault()
    if (onSet) {
      return onSet(!checked)
    }
    if (onChange) {
      onChange(name, !checked)
    }
    return false
  }

  const renderSwitchMode = useMemo(() => (
    <>
      <input
        type="checkbox"
        id={`switch${name}`}
        onChange={onFormChange}
        disabled={!!readOnly}
        checked={checked}
        className="tennders-notif-settings-input"
      />
      <label
        onClick={onToggle}
        htmlFor={`switch${name}`}
        data-on-label="Yes"
        data-off-label="No"
        className="tennders-notif-settings-label"
      />
    </>
  ), [name, readOnly, checked, onFormChange])

  const renderRadio = useMemo(() => (
    <div className={eClass || 'd-flex row'}>
      <input
        checked={form && `${radioValue}` === `${form[name]}`}
        type="radio"
        name={name}
        id={`radio_${name}`}
        value={radioValue}
        onClick={onFormChange}
        onChange={() => null}
        className="form-radio tennders-share-load-w-box-carriers-dont-show"
      />
      <label htmlFor={`radio_${name}`}>{label}</label>
    </div>
  ), [eClass, form, radioValue, name, onFormChange, label])

  const renderSlider = useMemo(() => (
    <div className={eClass || 'd-flex row'}>
      <label className={`${styles['checkbox-switch']} mr-6`} onClick={onToggle}>
        <input type="checkbox" className={`${styles.checkbox} mr-6`} checked={checked} />
        <span className={`${styles['checkbox-slider']} ${styles.round}`}></span>
        {children}
      </label>
      <div className="align-self-center">{label}</div>
    </div>
  ), [eClass, checked, onToggle, children, label])

  const renderOnlyInput = useMemo(() => (
    <>
      <input
        type="checkbox"
        onChange={onFormChange}
        disabled={!!readOnly}
        checked={checked}
        className={className || (`form-checkbox flex-none${inputError ? ' error' : ''}`)}
      />
      <label onClick={onToggle} className={labelClass}>{label}</label>
    </>
  ), [onFormChange, readOnly, checked, className, labelClass, label, inputError])

  const renderRegular = useMemo(() => (
    <div className={eClass || 'd-flex align-center'}>
      <input
        type="checkbox"
        onChange={onFormChange}
        disabled={!!readOnly}
        checked={checked}
        className={className || (`form-checkbox flex-none${inputError ? ' error' : ''}`)}
      />
      <label onClick={onToggle} className={labelClass}>{label}</label>
      {children}
    </div>
  ), [eClass, readOnly, onFormChange, checked, className, inputError, label, labelClass])

  const renderComponent = useMemo(() => {
    if (switchMode) {
      return renderSwitchMode
    }
    if (inputType === 'radio') {
      return renderRadio
    }
    if (inputType === 'slider') {
      return renderSlider
    }
    if (onlyInput) {
      return renderOnlyInput
    }
    return renderRegular
  }, [switchMode, inputType, onlyInput, renderSwitchMode, renderRadio, renderSlider, renderOnlyInput])

  return renderComponent
}

export default GCheckBox
